import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";

const testimonials = [
  {
    quote: "My daughter Meera saw the book and said 'Mumma, that's ME!' — I've never seen her so happy with any gift.",
    name: "Priya S.",
    location: "Bangalore",
    initials: "PS",
    color: "bg-rose-100 text-rose-600",
  },
  {
    quote: "Ordered for my son's 5th birthday. Every kid at the party wanted their own copy. Best Rs.499 I've ever spent.",
    name: "Rahul M.",
    location: "Mumbai",
    initials: "RM",
    color: "bg-amber-100 text-amber-600",
  },
  {
    quote: "We've bought 10 copies as return gifts. Parents keep messaging me asking where I got them!",
    name: "Sneha T.",
    location: "Hyderabad",
    initials: "ST",
    color: "bg-indigo-100 text-indigo-600",
  },
];

function AnimatedCounter({ target, label }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const duration = 2000;
          const steps = 60;
          const increment = target / steps;
          let current = 0;
          const timer = setInterval(() => {
            current += increment;
            if (current >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, duration / steps);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <div ref={ref} className="text-center">
      <div className="text-3xl sm:text-4xl font-bold text-rose-500" style={{ fontFamily: "'Fredoka', sans-serif" }}>
        {count.toLocaleString()}+
      </div>
      <p className="text-sm text-indigo-950/50 font-medium mt-1">{label}</p>
    </div>
  );
}

export default function SocialProof() {
  return (
    <section id="social-proof" className="py-20 sm:py-28 bg-rose-50/50 relative overflow-hidden" data-testid="social-proof-section">
      <div className="absolute top-10 right-0 w-96 h-96 bg-amber-100/30 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-bold text-rose-500 uppercase tracking-widest mb-3 block">Testimonials</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-indigo-950 tracking-tight mb-4" data-testid="social-proof-heading">
            Parents Can't Stop Sharing
          </h2>
        </motion.div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-3 gap-4 max-w-xl mx-auto mb-14 bg-white rounded-3xl p-6 border border-indigo-950/5 shadow-lg"
          data-testid="stats-counter"
        >
          <AnimatedCounter target={5000} label="Happy Parents" />
          <div className="text-center">
            <div className="flex items-center justify-center gap-1">
              <span className="text-3xl sm:text-4xl font-bold text-amber-500" style={{ fontFamily: "'Fredoka', sans-serif" }}>4.9</span>
              <Star className="w-6 h-6 text-amber-400 fill-amber-400" />
            </div>
            <p className="text-sm text-indigo-950/50 font-medium mt-1">Average Rating</p>
          </div>
          <AnimatedCounter target={2500} label="Books Created" />
        </motion.div>

        {/* Testimonial cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="bg-white rounded-3xl p-6 border border-indigo-950/5 shadow-lg shadow-rose-900/5 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              data-testid={`testimonial-card-${i + 1}`}
            >
              <Quote className="w-8 h-8 text-rose-200 mb-4" />
              <p className="text-sm sm:text-base text-indigo-950/70 leading-relaxed mb-5 italic">
                "{t.quote}"
              </p>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full ${t.color} flex items-center justify-center font-bold text-sm`}>
                  {t.initials}
                </div>
                <div>
                  <p className="text-sm font-bold text-indigo-950">{t.name}</p>
                  <p className="text-xs text-indigo-950/40">{t.location}</p>
                </div>
                <div className="ml-auto flex gap-0.5">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust badges row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="flex flex-wrap justify-center gap-6 mt-12 text-sm text-indigo-950/40 font-medium"
          data-testid="trust-badges-row"
        >
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/></svg>
            Secure Payment
          </span>
          <span>|</span>
          <span>Free Shipping above Rs.999</span>
          <span>|</span>
          <span>100% Satisfaction Guarantee</span>
        </motion.div>
      </div>
    </section>
  );
}
